/**
 * PageGlobe — viewport-level wireframe sphere with rotating
 * meridians and orbiting satellites. Mounted at the App root so it
 * spans the true viewport edge-to-edge, NOT bound by main's
 * max-w-[1400px] container.
 *
 * Each section that scrolls past determines whether the globe shows
 * through:
 *   - hero (transparent bg)            → globe visible
 *   - stats band (solid brand-blue)    → covers the globe in its area
 *   - how-it-works (transparent bg)    → bottom of globe peeks through
 *
 * Section boundaries become the natural visual cuts — no forced
 * gradient transitions because the globe and the section bgs are
 * independent layers, not a single forced blend.
 *
 * Position is `position: absolute` relative to the root flex column
 * (which has `relative` + `overflow-x-hidden`), so the globe pins to
 * the top of the page and scrolls with it (i.e. NOT fixed-position
 * wallpaper — the globe is bound to the page top and disappears as
 * you scroll past the hero).
 */

interface Satellite {
  fromAngle: number;
  radius: number;
  dotR: number;
  dur: number;
  direction: 1 | -1;
}

const SATELLITES: readonly Satellite[] = [
  { fromAngle:   0, radius: 410, dotR: 8,   dur: 14, direction:  1 },
  { fromAngle:  72, radius: 460, dotR: 5,   dur: 22, direction: -1 },
  { fromAngle: 144, radius: 395, dotR: 6,   dur: 18, direction:  1 },
  { fromAngle: 216, radius: 480, dotR: 7,   dur: 28, direction: -1 },
  { fromAngle: 288, radius: 425, dotR: 4.5, dur: 16, direction:  1 },
  { fromAngle:  36, radius: 510, dotR: 5,   dur: 32, direction: -1 },
];

export function PageGlobe() {
  return (
    <div
      aria-hidden
      className="absolute pointer-events-none select-none hidden lg:block"
      style={{
        top: '20px',
        right: 'clamp(-360px, -10vw, -120px)',
        width: 'clamp(640px, 80vw, 1400px)',
        height: 'clamp(640px, 80vw, 1400px)',
        zIndex: 0,
        opacity: 0.35,
      }}
    >
      <svg viewBox="-560 -560 1120 1120" className="w-full h-full">
        {/* Globe wireframe + rotating meridians */}
        <g>
          <circle r="360" fill="none" stroke="var(--color-primary)" strokeWidth="2" />
          <line x1="0" y1="-360" x2="0" y2="360" stroke="var(--color-primary)" strokeWidth="1.5" />
          <line x1="-360" y1="0" x2="360" y2="0" stroke="var(--color-primary)" strokeWidth="1.5" />
          <ellipse cx="0" cy="0" rx="360" ry="80"  fill="none" stroke="var(--color-primary)" strokeWidth="1.3" />
          <ellipse cx="0" cy="0" rx="360" ry="180" fill="none" stroke="var(--color-primary)" strokeWidth="1.3" />
          <ellipse cx="0" cy="0" rx="360" ry="270" fill="none" stroke="var(--color-primary)" strokeWidth="1.3" />
          <ellipse cx="0" cy="0" rx="120" ry="360" fill="none" stroke="var(--color-primary)" strokeWidth="1.3">
            <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="40s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="0" cy="0" rx="225" ry="360" fill="none" stroke="var(--color-primary)" strokeWidth="1.3">
            <animateTransform attributeName="transform" type="rotate" from="60 0 0" to="420 0 0" dur="55s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="0" cy="0" rx="300" ry="360" fill="none" stroke="var(--color-primary)" strokeWidth="1.2">
            <animateTransform attributeName="transform" type="rotate" from="120 0 0" to="480 0 0" dur="70s" repeatCount="indefinite" />
          </ellipse>
          {/* Static connection-network nodes on the globe */}
          <circle cx="-280" cy="-110" r="7"   fill="var(--color-primary)" />
          <circle cx="160"  cy="-200" r="7"   fill="var(--color-primary)" />
          <circle cx="260"  cy="100"  r="7.5" fill="var(--color-primary)" />
          <circle cx="-130" cy="260"  r="6.5" fill="var(--color-primary)" />
          <circle cx="-220" cy="90"   r="6"   fill="var(--color-primary)" />
          <circle cx="70"   cy="-300" r="6"   fill="var(--color-primary)" />
          <circle cx="300"  cy="-80"  r="5.5" fill="var(--color-primary)" />
          <line x1="-280" y1="-110" x2="160"  y2="-200" stroke="var(--color-primary)" strokeWidth="1" strokeDasharray="5,5" opacity="0.55" />
          <line x1="160"  y1="-200" x2="260"  y2="100"  stroke="var(--color-primary)" strokeWidth="1" strokeDasharray="5,5" opacity="0.55" />
          <line x1="260"  y1="100"  x2="-130" y2="260"  stroke="var(--color-primary)" strokeWidth="1" strokeDasharray="5,5" opacity="0.55" />
          <line x1="-220" y1="90"   x2="70"   y2="-300" stroke="var(--color-primary)" strokeWidth="1" strokeDasharray="5,5" opacity="0.55" />
        </g>

        {/* Orbiting satellites */}
        {SATELLITES.map((s, i) => {
          const start = s.direction === 1 ? s.fromAngle : -s.fromAngle;
          const end = s.direction === 1 ? s.fromAngle + 360 : -s.fromAngle - 360;
          const initX = s.radius * Math.cos((s.fromAngle * Math.PI) / 180);
          const initY = s.radius * Math.sin((s.fromAngle * Math.PI) / 180);
          return (
            <g key={i}>
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`${start} 0 0`}
                to={`${end} 0 0`}
                dur={`${s.dur}s`}
                repeatCount="indefinite"
              />
              <circle cx={initX} cy={initY} r={s.dotR * 2.2} fill="var(--color-primary)" opacity="0.18" />
              <circle cx={initX} cy={initY} r={s.dotR} fill="var(--color-primary)" />
            </g>
          );
        })}
      </svg>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          svg animateTransform { display: none; }
        }
      `}</style>
    </div>
  );
}
